// Explosion lifecycle: a blast expands to full radius, holds, then contracts.
// Only while radius > 0 does it intercept (see collision.ts).

import type { Explosion, ExplosionPhase, ExplosionSource, Vec2 } from '../state/types';
import type { AdvanceExplosions } from './contracts';
import {
  EXPLOSION_CONTRACT_T,
  EXPLOSION_EXPAND_T,
  EXPLOSION_HOLD_T,
  EXPLOSION_MAX_AGE,
  EXPLOSION_MAX_RADIUS,
  FIXED_DT,
} from '../constants';

export interface RadiusSample {
  readonly radius: number;
  readonly phase: ExplosionPhase;
}

export function radiusAt(age: number): RadiusSample {
  if (age < EXPLOSION_EXPAND_T) {
    return {
      radius: EXPLOSION_MAX_RADIUS * (age / EXPLOSION_EXPAND_T),
      phase: 'expanding',
    };
  }
  if (age < EXPLOSION_EXPAND_T + EXPLOSION_HOLD_T) {
    return { radius: EXPLOSION_MAX_RADIUS, phase: 'holding' };
  }
  const t = (age - (EXPLOSION_EXPAND_T + EXPLOSION_HOLD_T)) / EXPLOSION_CONTRACT_T;
  return { radius: Math.max(0, EXPLOSION_MAX_RADIUS * (1 - t)), phase: 'contracting' };
}

/** A newborn blast: radius 0 (so a chained explosion can't instantly clear the screen). */
export function createExplosion(
  id: number,
  pos: Vec2,
  source: ExplosionSource,
): Explosion {
  return {
    id,
    pos,
    age: 0,
    maxAge: EXPLOSION_MAX_AGE,
    radius: 0,
    phase: 'expanding',
    source,
  };
}

export const advanceExplosions: AdvanceExplosions = (state) => {
  if (state.explosions.length === 0) return state;
  const explosions = state.explosions
    .map((e) => {
      const age = e.age + FIXED_DT;
      const { radius, phase } = radiusAt(age);
      return { ...e, age, radius, phase };
    })
    .filter((e) => e.age < e.maxAge);
  return { ...state, explosions };
};
